import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, EyeOff, Settings, X } from "lucide-react";
import vocabulariumBlason from "../assets/vocabularium_blason.png";
import CircleCheckIcon from "./IconComponent/CircleCheckIcon";
import { API_URL } from "../config/api";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import {
  fetchQuestEmailPreference,
  saveQuestEmailPreference,
} from "../utils/questApi";
import { useAuth } from "./AuthGate";
import MenuIconTooltip from "./MenuIconTooltip";
import { FRAME_LAUNCHER_TOOLTIPS, MENU_LIBRARY_TOOLTIPS } from "../constants/menuTooltips";
import {
  TEXT_SCALES,
  formatTextScaleLabel,
  useAccountTextScale,
} from "../constants/textScalePreference";
import "./DeleteWordModal.css";
import "./RegisterSettingsModal.css";
import "./EntrainementVoc.css";
import "./PlayerMfaPanel.css";

const DEFAULT_EMAIL_PREF = {
  enabled: true,
  canChange: true,
  nextChangeAt: null,
};

function readFrameBox() {
  const frame = document.querySelector(".custom-pattern");
  if (!frame) return { left: 0, top: 0, width: 0, height: 0, scale: 1 };
  const rect = frame.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    scale: getAppScale(),
  };
}

function RadioChip({ name, label, checked, onChange, disabled = false }) {
  return (
    <label
      className={`entrainement-chip entrainement-chip--round${
        checked ? " entrainement-chip--checked" : ""
      }${disabled ? " entrainement-chip--disabled" : ""}`}
    >
      <input
        type="radio"
        className="entrainement-chip__input"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

function formatEmailPrefCooldown(nextChangeAt) {
  const target = new Date(nextChangeAt).getTime();
  if (!Number.isFinite(target)) {
    return "Ce choix ne peut être modifié qu'une fois toutes les 24 heures.";
  }
  const hours = Math.max(1, Math.ceil(Math.max(0, target - Date.now()) / (60 * 60 * 1000)));
  if (hours >= 24) {
    return "Ce choix pourra être modifié à nouveau dans 24 heures.";
  }
  if (hours === 1) {
    return "Ce choix pourra être modifié à nouveau dans moins d'une heure.";
  }
  return `Ce choix pourra être modifié à nouveau dans ${hours} heures.`;
}

async function mfaJson(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || "Impossible de régler l'authentification renforcée.");
  }
  return json;
}

export default function PlayerMfaPanel({ reading = false, onOpenSearch = () => {} }) {
  const { user } = useAuth();
  const [textScale, setTextScale] = useAccountTextScale(user?.sub || "");
  const [open, setOpen] = useState(false);
  const [frameBox, setFrameBox] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    scale: 1,
  });
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [secret, setSecret] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [mode, setMode] = useState("status");
  const [emailPref, setEmailPref] = useState(DEFAULT_EMAIL_PREF);
  const [draftEmailEnabled, setDraftEmailEnabled] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState("");

  useLayoutEffect(() => {
    const update = () => setFrameBox(readFrameBox());
    update();
    const frame = document.querySelector(".custom-pattern");
    const observer =
      frame && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (frame && observer) observer.observe(frame);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setEmailError("");
    setMode("status");
    setSecret("");
    setQrDataUrl("");
    setCode("");
    setPassword("");
    setPasswordVisible(false);
    mfaJson("/auth/mfa/status")
      .then((json) => {
        if (!cancelled) setEnabled(json.enabled === true);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    setEmailLoading(true);
    fetchQuestEmailPreference()
      .then((pref) => {
        if (cancelled) return;
        setEmailPref(pref);
        setDraftEmailEnabled(pref.enabled);
      })
      .catch(() => {
        if (cancelled) return;
        setEmailPref(DEFAULT_EMAIL_PREF);
        setDraftEmailEnabled(true);
      })
      .finally(() => {
        if (!cancelled) setEmailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!user?.sub) return null;
  const portalRoot = getAppPortalRoot();
  if (!portalRoot) return null;

  const scale = frameBox.scale || 1;
  const toggleStyle = {
    left: `${frameBox.left}px`,
    top: `${frameBox.top + frameBox.height / 2 + (reading ? -39 : 117) * scale}px`,
  };
  const emailLocked = !emailPref.canChange || emailBusy || busy;
  const contentLoading = loading || emailLoading;

  const close = () => {
    if (busy) return;
    setOpen(false);
    setSecret("");
    setQrDataUrl("");
    setCode("");
    setPassword("");
    setPasswordVisible(false);
    setError("");
    setEmailError("");
    setMode("status");
  };

  const chooseEmail = async (nextEnabled) => {
    if (emailLocked || nextEnabled === emailPref.enabled) {
      setDraftEmailEnabled(emailPref.enabled);
      return;
    }
    setDraftEmailEnabled(nextEnabled);
    setEmailBusy(true);
    setEmailError("");
    try {
      const next = await saveQuestEmailPreference(nextEnabled);
      setEmailPref(next);
      setDraftEmailEnabled(next.enabled);
    } catch (err) {
      if (err?.payload) {
        setEmailPref({
          enabled: err.payload.enabled !== false,
          canChange: err.payload.canChange === true,
          nextChangeAt: err.payload.nextChangeAt || null,
        });
        setDraftEmailEnabled(err.payload.enabled !== false);
      } else {
        setDraftEmailEnabled(emailPref.enabled);
      }
      setEmailError(err.message || "Impossible d'enregistrer ce choix.");
    } finally {
      setEmailBusy(false);
    }
  };

  const startSetup = async () => {
    setBusy(true);
    setError("");
    try {
      const json = await mfaJson("/auth/mfa/setup", { method: "POST", body: "{}" });
      setSecret(json.secret || "");
      setQrDataUrl(json.qrDataUrl || "");
      setCode("");
      setMode("enroll");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const json = await mfaJson("/auth/mfa/confirm", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      setEnabled(json.enabled === true);
      setSecret("");
      setQrDataUrl("");
      setCode("");
      setMode("status");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const json = await mfaJson("/auth/mfa/disable", {
        method: "POST",
        body: JSON.stringify({ password, code: code.trim() }),
      });
      setEnabled(json.enabled === true);
      setPassword("");
      setPasswordVisible(false);
      setCode("");
      setMode("status");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <>
      <div className="player-mfa">
        <MenuIconTooltip
          tooltip={reading ? MENU_LIBRARY_TOOLTIPS.search : FRAME_LAUNCHER_TOOLTIPS.preferences}
          placement="below"
          portal
          className="player-mfa__tip"
          style={toggleStyle}
        >
          {reading ? (
            <button
              type="button"
              className="player-mfa__toggle"
              aria-label={MENU_LIBRARY_TOOLTIPS.search}
              onClick={onOpenSearch}
            >
              <span className="player-mfa__search-icon" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="player-mfa__toggle"
              aria-label={FRAME_LAUNCHER_TOOLTIPS.preferences}
              aria-expanded={open}
              onClick={() => setOpen(true)}
            >
              <Settings className="player-mfa__toggle-icon" strokeWidth={1} aria-hidden="true" />
            </button>
          )}
        </MenuIconTooltip>
      </div>
      {open ? (
        <div
          className="delete-word-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            className="delete-word-modal player-profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="player-profile-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="delete-word-modal__close"
              onClick={close}
              aria-label="Fermer"
              disabled={busy}
            >
              <X size={18} strokeWidth={2} />
            </button>
            <h2 id="player-profile-title" className="player-profile-modal__title">
              Préférences
            </h2>
            <div
              className="player-profile-modal__body"
              aria-busy={contentLoading ? true : undefined}
            >
              <section className="register-settings-modal__section">
                <h3 className="register-settings-modal__section-title">Taille du texte</h3>
                <div
                  className="player-profile-modal__text-scales"
                  role="radiogroup"
                  aria-label="Taille du texte"
                >
                  {TEXT_SCALES.map((scale) => (
                    <RadioChip
                      key={scale}
                      name="profile-text-scale"
                      label={formatTextScaleLabel(scale)}
                      checked={textScale === scale}
                      onChange={() => setTextScale(scale)}
                    />
                  ))}
                </div>
              </section>
              <section className="register-settings-modal__section">
                <h3 className="register-settings-modal__section-title">Mail de quête</h3>
                {contentLoading ? (
                  <div className="player-profile-modal__load" aria-hidden="true">
                    <span className="player-profile-modal__load-line" />
                    <span className="player-profile-modal__load-line player-profile-modal__load-line--short" />
                    <span className="player-profile-modal__load-chips">
                      <span className="player-profile-modal__load-chip" />
                      <span className="player-profile-modal__load-chip" />
                    </span>
                    <span className="player-profile-modal__load-line player-profile-modal__load-line--medium" />
                  </div>
                ) : (
                  <>
                    <p className="register-settings-modal__help">
                      Recevoir la quête du jour par mail. La quête restera toujours visible dans
                      l&apos;application.
                    </p>
                    <div
                      className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
                      role="radiogroup"
                      aria-label="Mail de quête"
                    >
                      <RadioChip
                        name="profile-quest-email-enabled"
                        label="oui"
                        checked={draftEmailEnabled}
                        disabled={emailLocked}
                        onChange={() => chooseEmail(true)}
                      />
                      <RadioChip
                        name="profile-quest-email-enabled"
                        label="non"
                        checked={!draftEmailEnabled}
                        disabled={emailLocked}
                        onChange={() => chooseEmail(false)}
                      />
                    </div>
                    <p className="register-settings-modal__help-line">
                      {emailPref.canChange
                        ? "Ce choix ne peut être modifié qu'une fois toutes les 24 heures."
                        : formatEmailPrefCooldown(emailPref.nextChangeAt)}
                    </p>
                    {emailError ? (
                      <p className="register-settings-modal__help-line register-settings-modal__help-line--error">
                        {emailError}
                      </p>
                    ) : null}
                  </>
                )}
              </section>

              <div className="player-profile-modal__separator" />

              <section
                className="register-settings-modal__section"
                aria-busy={busy && mode === "status" ? true : undefined}
              >
                <h3 className="register-settings-modal__section-title">
                  Authentification renforcée
                </h3>
                {contentLoading ? (
                  <div className="player-profile-modal__load" aria-hidden="true">
                    <span className="player-profile-modal__load-line" />
                    <span className="player-profile-modal__load-line player-profile-modal__load-line--short" />
                    <span className="player-profile-modal__load-btn" />
                  </div>
                ) : null}
                {!contentLoading && mode === "status" && enabled === false && busy ? (
                  <form className="player-profile-modal__form" aria-hidden="true">
                    <ol className="player-profile-modal__steps">
                      <li className="player-profile-modal__step">
                        <span className="player-profile-modal__step-num player-profile-modal__skel-bone" />
                        <p>
                          <span className="player-profile-modal__skel-ink">
                            Installer sur votre smartphone une application d&apos;authentification
                            (Google Authenticator).
                          </span>
                        </p>
                      </li>
                      <li className="player-profile-modal__step">
                        <span className="player-profile-modal__step-num player-profile-modal__skel-bone" />
                        <p>
                          <span className="player-profile-modal__skel-ink">
                            Scannez le code QR ci-dessous.
                          </span>
                        </p>
                      </li>
                    </ol>
                    <div className="player-profile-modal__qr player-profile-modal__skel-bone" />
                    <p className="player-profile-modal__secret">
                      <span className="player-profile-modal__skel-ink">
                        Si le scan échoue, utilisez la clé suivante dans votre application
                        d&apos;authentification :
                      </span>
                      <code>
                        <span className="player-profile-modal__skel-ink">
                          GGRAHHLGPNBL7RYDS5FQ66MM4XRNVZYPFGSO5MBA4KAHAQKMJ4TA
                        </span>
                      </code>
                    </p>
                    <ol className="player-profile-modal__steps" start={3}>
                      <li className="player-profile-modal__step">
                        <span className="player-profile-modal__step-num player-profile-modal__skel-bone" />
                        <p>
                          <span className="player-profile-modal__skel-ink">
                            Saisissez le code à 6 chiffres qui s&apos;affiche dans l&apos;application
                            de votre smartphone.
                          </span>
                        </p>
                      </li>
                    </ol>
                    <input
                      className="delete-word-modal__input player-profile-modal__code player-profile-modal__skel-bone"
                      value="000000"
                      readOnly
                      tabIndex={-1}
                    />
                    <div className="player-profile-modal__actions">
                      <button
                        type="button"
                        tabIndex={-1}
                        className="delete-word-modal__btn delete-word-modal__btn--cancel player-profile-modal__skel-bone"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        tabIndex={-1}
                        className="delete-word-modal__btn delete-word-modal__btn--primary player-profile-modal__skel-bone"
                      >
                        Valider
                      </button>
                    </div>
                  </form>
                ) : null}
                {!contentLoading && mode === "status" && enabled === false && !busy ? (
                  <>
                    <p className="player-profile-modal__hint">
                      Désactivée. À la connexion, seul votre mot de passe est demandé. Vous pouvez
                      l'activer avec une application comme Google Authenticator.
                    </p>
                    <button
                      type="button"
                      className="delete-word-modal__btn delete-word-modal__btn--primary player-profile-modal__action"
                      onClick={startSetup}
                    >
                      Activer
                    </button>
                  </>
                ) : null}
                {!contentLoading && mode === "status" && enabled === true ? (
                  <>
                    <p className="player-profile-modal__hint player-profile-modal__status">
                      <CircleCheckIcon
                        size={22}
                        className="player-profile-modal__status-check"
                        style={{ color: "#166534" }}
                      />
                      <span>
                        Activé. Un code généré par votre application d'authentification vous sera
                        demandé à chaque connexion en plus de votre mot de passe.
                      </span>
                    </p>
                    <button
                      type="button"
                      className="delete-word-modal__btn delete-word-modal__btn--cancel player-profile-modal__action"
                      onClick={() => {
                        setError("");
                        setCode("");
                        setPassword("");
                        setPasswordVisible(false);
                        setMode("disable");
                      }}
                    >
                      Désactiver
                    </button>
                  </>
                ) : null}
                {mode === "enroll" ? (
                  <form className="player-profile-modal__form" onSubmit={confirmSetup}>
                    <ol className="player-profile-modal__steps">
                      <li className="player-profile-modal__step">
                        <span className="player-profile-modal__step-num">1</span>
                        <p>
                          Installer sur votre smartphone une application d'authentification (Google
                          Authenticator).
                        </p>
                      </li>
                      <li className="player-profile-modal__step">
                        <span className="player-profile-modal__step-num">2</span>
                        <p>Scannez le code QR ci-dessous.</p>
                      </li>
                    </ol>
                    {qrDataUrl ? (
                      <img className="player-profile-modal__qr" src={qrDataUrl} alt="Code à scanner" />
                    ) : null}
                    {secret ? (
                      <p className="player-profile-modal__secret">
                        Si le scan échoue, utilisez la clé suivante dans votre application
                        d'authentification :
                        <code>{secret}</code>
                      </p>
                    ) : null}
                    <ol className="player-profile-modal__steps" start={3}>
                      <li className="player-profile-modal__step">
                        <span className="player-profile-modal__step-num">3</span>
                        <p>
                          Saisissez le code à 6 chiffres qui s'affiche dans l'application de votre
                          smartphone.
                        </p>
                      </li>
                    </ol>
                    <input
                      className="delete-word-modal__input player-profile-modal__code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      aria-label="Code à 6 chiffres"
                      required
                    />
                    <div className="player-profile-modal__actions">
                      <button
                        type="button"
                        className="delete-word-modal__btn delete-word-modal__btn--cancel"
                        onClick={() => {
                          setMode("status");
                          setSecret("");
                          setQrDataUrl("");
                          setCode("");
                          setError("");
                        }}
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="delete-word-modal__btn delete-word-modal__btn--primary"
                        disabled={busy || code.length !== 6}
                      >
                        {busy ? "Vérification…" : "Valider"}
                      </button>
                    </div>
                  </form>
                ) : null}
                {mode === "disable" ? (
                  <form className="player-profile-modal__form" onSubmit={disableMfa}>
                    <p className="player-profile-modal__hint">Saisissez votre mot de passe.</p>
                    <div className="player-profile-modal__password">
                      <input
                        className="delete-word-modal__input player-profile-modal__password-input"
                        type={passwordVisible ? "text" : "password"}
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Mot de passe"
                        required
                      />
                      <button
                        type="button"
                        className="player-profile-modal__peek"
                        aria-label={
                          passwordVisible ? "Masquer le mot de passe" : "Afficher le mot de passe"
                        }
                        aria-pressed={passwordVisible}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => setPasswordVisible((visible) => !visible)}
                      >
                        {passwordVisible ? (
                          <Eye size={18} strokeWidth={1.75} aria-hidden="true" />
                        ) : (
                          <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    <p className="player-profile-modal__hint">
                      Puis, saisissez le code actuel à 6 chiffres de votre application
                      d'authentification :
                    </p>
                    <input
                      className="delete-word-modal__input player-profile-modal__code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      aria-label="Code à 6 chiffres"
                      required
                    />
                    <div className="player-profile-modal__actions">
                      <button
                        type="button"
                        className="delete-word-modal__btn delete-word-modal__btn--cancel"
                        onClick={() => {
                          setMode("status");
                          setPassword("");
                          setPasswordVisible(false);
                          setCode("");
                          setError("");
                        }}
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="delete-word-modal__btn delete-word-modal__btn--confirm"
                        disabled={busy || !password || code.length !== 6}
                      >
                        {busy ? "Désactivation…" : "Désactiver"}
                      </button>
                    </div>
                  </form>
                ) : null}
                {!contentLoading && error ? (
                  <p className="player-profile-modal__error">{error}</p>
                ) : null}
              </section>
            </div>
            {mode === "status" && !(enabled === false && busy) ? (
              <img
                className="player-profile-modal__blason"
                src={vocabulariumBlason}
                alt="Blason de Vocabularium"
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </>,
    portalRoot
  );
}
